import { useMemo, useState } from "react";
import { FilterBar } from "@/shared/components/filter-bar";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { ArrowDown, ArrowUp, Loader2, X } from "lucide-react";
import {
  TemplateStepCommandDialog,
  type TemplateStepCommand,
} from "./template-step-command-dialog";

type SectionPaletteControlsProps = {
  search: string;
  onSearchChange: (value: string) => void;
  showGlobal: boolean;
  onToggleGlobal: (show: boolean) => void;
  insertDisabled: boolean;
  isInserting: boolean;
  stepsCount: number;
  onInsertTop: () => Promise<unknown>;
  onInsertBottom: () => Promise<unknown>;
  onInsertAt: (position: number) => Promise<unknown>;
  onClearSelection: () => void;
  hasSelection: boolean;
  selectedStepName: string;
};

export function SectionPaletteControls({
  search,
  onSearchChange,
  showGlobal,
  onToggleGlobal,
  insertDisabled,
  isInserting,
  stepsCount,
  onInsertTop,
  onInsertBottom,
  onInsertAt,
  onClearSelection,
  hasSelection,
  selectedStepName,
}: SectionPaletteControlsProps) {
  const positions = useMemo(
    () =>
      Array.from({ length: Math.max(stepsCount + 1, 1) }, (_, i) => `${i + 1}`),
    [stepsCount],
  );
  const [selectedPosition, setSelectedPosition] = useState<string>(
    positions[0] ?? "1",
  );
  // Derive the effective position during render instead of syncing state in
  // an effect: if the selection is out of range (steps were removed), clamp
  // to the last valid position.
  const insertPosition = positions.includes(selectedPosition)
    ? selectedPosition
    : (positions[positions.length - 1] ?? "1");
  const [command, setCommand] = useState<TemplateStepCommand>();

  const confirmInsert = async () => {
    if (command === "insert-top") return onInsertTop();
    if (command === "insert-bottom") return onInsertBottom();
    if (command === "insert-at") return onInsertAt(Number(insertPosition));
  };

  return (
    <FilterBar
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search steps or templates..."
    >
      <div className="flex w-full flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-md border bg-muted/40 p-0.5">
          <Button
            size="sm"
            variant={showGlobal ? "secondary" : "ghost"}
            onClick={() => onToggleGlobal(true)}
            className="h-7 px-2.5"
          >
            Mine + Global
          </Button>
          <Button
            size="sm"
            variant={!showGlobal ? "secondary" : "ghost"}
            onClick={() => onToggleGlobal(false)}
            className="h-7 px-2.5"
          >
            Mine only
          </Button>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={insertDisabled}
            onClick={() => setCommand("insert-top")}
          >
            {isInserting ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="mr-1 h-4 w-4" />
            )}
            Insert top
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={insertDisabled}
            onClick={() => setCommand("insert-bottom")}
          >
            {isInserting ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <ArrowDown className="mr-1 h-4 w-4" />
            )}
            Insert bottom
          </Button>
          <Select
            value={insertPosition}
            onValueChange={setSelectedPosition}
            disabled={insertDisabled}
          >
            <SelectTrigger className="h-8 min-w-36">
              <SelectValue placeholder="Position" />
            </SelectTrigger>
            <SelectContent align="end">
              {positions.map((pos) => (
                <SelectItem key={pos} value={pos}>
                  Position {pos}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            disabled={insertDisabled}
            onClick={() => setCommand("insert-at")}
          >
            Insert at position
          </Button>
          {hasSelection ? (
            <Button size="sm" variant="ghost" onClick={onClearSelection}>
              <X className="mr-1 h-4 w-4" />
              Clear
            </Button>
          ) : null}
        </div>
      </div>
      <TemplateStepCommandDialog
        open={Boolean(command)}
        command={command ?? "insert-at"}
        sectionName={selectedStepName}
        isPending={isInserting}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setCommand(undefined);
        }}
        onConfirm={confirmInsert}
      />
    </FilterBar>
  );
}
